import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import MovieCard from './MovieCard';

const MovieCarousel = ({ title, movies, onShowDetails }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  // Check scroll position
  const checkScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  // Update scroll position on mount and resize
  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, [checkScrollPosition, movies]);

  // Scroll handlers
  const scroll = useCallback((direction) => {
    if (!scrollRef.current) return;

    const scrollAmount = scrollRef.current.clientWidth * 0.75;
    const newPosition = scrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);

    scrollRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth',
    });
  }, []);

  // Handle scroll event
  const handleScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  if (!movies || movies.length === 0) return null;

  return (
    <div
      className="relative py-4 md:py-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Section Title */}
      <div className="px-4 sm:px-6 lg:px-12 mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-black text-white flex items-center gap-3 uppercase tracking-wide">
          <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center shadow-lg shadow-red-900/50">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 2h6v4H7V5zm8 8v2h1v-2h-1zm-2-2H7v4h6v-4zm2 0h1V9h-1v2zm1-4V5h-1v2h1zM5 5v2H4V5h1zm0 4H4v2h1V9zm-1 4h1v2H4v-2z" clipRule="evenodd" />
            </svg>
          </div>
          {title}
        </h2>
      </div>

      {/* Carousel Container */}
      <div className="relative group/carousel">
        {/* Left Navigation Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => scroll('left')}
          className={cn(
            'absolute left-0 top-1/2 -translate-y-1/2 z-40',
            'w-10 h-24 md:w-12 md:h-32 rounded-none rounded-r-md',
            'bg-black/50 hover:bg-black/80 backdrop-blur-sm',
            'text-white hover:text-blue-500',
            'transition-all duration-300',
            'hidden md:flex items-center justify-center',
            canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <ChevronLeft className="w-8 h-8" />
        </Button>

        {/* Right Navigation Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => scroll('right')}
          className={cn(
            'absolute right-0 top-1/2 -translate-y-1/2 z-40',
            'w-10 h-24 md:w-12 md:h-32 rounded-none rounded-l-md',
            'bg-black/50 hover:bg-black/80 backdrop-blur-sm',
            'text-white hover:text-blue-500',
            'transition-all duration-300',
            'hidden md:flex items-center justify-center',
            canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <ChevronRight className="w-8 h-8" />
        </Button>

        {/* Scrollable Container */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={cn(
            'flex gap-3 md:gap-4 overflow-x-auto carousel-scroll',
            'px-4 sm:px-6 lg:px-12',
            'snap-x snap-mandatory',
            'scroll-smooth'
          )}
        >
          {movies.map((movie, index) => (
            <div key={movie.id} className="snap-start">
              <MovieCard
                movie={movie}
                onShowDetails={onShowDetails}
                index={index}
              />
            </div>
          ))}

          {/* Spacer at end */}
          <div className="flex-shrink-0 w-4 sm:w-6 lg:w-12" />
        </div>

        {/* Gradient Edges */}
        <div
          className={cn(
            'absolute left-0 top-0 bottom-0 w-8 md:w-12 pointer-events-none',
            'bg-gradient-to-r from-background to-transparent',
            'transition-opacity duration-300',
            canScrollLeft ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div
          className={cn(
            'absolute right-0 top-0 bottom-0 w-8 md:w-12 pointer-events-none',
            'bg-gradient-to-l from-background to-transparent',
            'transition-opacity duration-300',
            canScrollRight ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    </div>
  );
};

export default MovieCarousel;
